import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import Avatar from '../shared/Avatar';
import CountryDisplay from '../shared/CountryDisplay';
import { boutCorner, type BoutCornerColour } from '../../utils/boutCorner';
import { weightText } from '../../utils/weight';
import type { Bout } from '../../types';

interface BoutCornersProps {
  bout: Bout;
}

/** The colour of the header of each corner. The red column has the border
 *  between the two columns. */
const CORNER_TONE: Record<BoutCornerColour, { head: string; column: string }> = {
  red: { head: 'bg-red-600', column: 'border-r border-gray-100' },
  blue: { head: 'bg-blue-600', column: '' },
};

/** One corner of the bout page: the header with the photo, the name and the
 *  team, then the facts. The red and the blue corner share this markup, so a
 *  change of the card (#552, #622) is made once. */
function CornerColumn({ bout, corner }: { bout: Bout; corner: BoutCornerColour }) {
  const { t } = useI18n();
  const { boutCount, ageShort, genderLabel } = useLabels();
  const c = boutCorner(bout, corner);
  const kg = weightText(c.weight);

  return (
    <div className={CORNER_TONE[corner].column}>
      {/* #552 — who fights is the main information of the page. On a
          phone the avatar is hidden and the names wrap: no name is cut. */}
      <div className={`${CORNER_TONE[corner].head} px-3 sm:px-4 py-3 text-white`}>
        <div className="flex items-center gap-3">
          <div className="hidden sm:block shrink-0">
            {c.photo
              ? <img src={c.photo} alt="" referrerPolicy="no-referrer"
                  className="block w-10 h-10 rounded-full object-cover border-2 border-white/30" />
              : <Avatar firstName={c.first || '?'} lastName={c.last || '?'}
                  size="md" className="ring-2 ring-white/30" />}
          </div>
          <div className="min-w-0">
            <div className="font-black text-base leading-tight break-words">
              {c.athleteRecordId
                ? <Link to={`/athletes/${c.athleteRecordId}`} data-testid={`corner-athlete-${corner}`} className="hover:underline">{c.last}</Link>
                : c.last}
            </div>
            <div className="font-normal text-sm opacity-90 flex items-center gap-1.5">
              <span className="min-w-0 break-words">{c.first}</span>
              {c.country && <CountryDisplay code={c.country} size="sm" className="shrink-0" />}
            </div>
            {c.team && <div className="text-xs opacity-75 mt-0.5 break-words">{c.team}</div>}
          </div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-1 text-xs text-gray-600">
        {c.gender && <div><span className="text-gray-400">{t.common.gender}:</span> {genderLabel(c.gender)}</div>}
        {!!c.age && <div><span className="text-gray-400">{t.common.age}:</span> {ageShort(c.age)}</div>}
        {kg && <div><span className="text-gray-400">{t.common.weight}:</span> {kg}</div>}
        {/* #622 — sent only to the roles of the Officials menu. */}
        {c.experience != null && (
          <div data-testid={`corner-experience-${corner}`} data-experience={c.experience}>
            <span className="text-gray-400">{t.bout.experienceLabel}:</span> {c.experienceEstimated ? '≈ ' : ''}{boutCount(c.experience)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BoutCorners({ bout }: BoutCornersProps) {
  return (
    <div className="card overflow-hidden">
      <div className="grid grid-cols-2">
        {(['red', 'blue'] as const).map(corner => <CornerColumn key={corner} bout={bout} corner={corner} />)}
      </div>
    </div>
  );
}
